"use client";

import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Box,
  IconButton,
  LinearProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
} from "@mui/material";
import {
  ChevronLeft,
  ChevronRight,
  Close,
  Share,
  Download,
  Pause,
  PlayArrow,
  Replay,
  EmojiEvents,
  Group,
  Smartphone,
} from "@mui/icons-material";
import WelcomeSlide from "./year-in-review/welcome-slide";
import ClientGrowthSlide from "./year-in-review/client-growth-slide";
import ContentCreatedSlide from "./year-in-review/content-created-slide";
import NutritionTargetsSlide from "./year-in-review/nutrition-targets-slide";
import ClientActivitySlide from "./year-in-review/client-activity-slide";
import ClientAwardSlide from "./year-in-review/client-award-slide";
import ThankYouSlide from "./year-in-review/thank-you-slide";
import yearInReviewData from "./year-in-review/year-in-review-data.json";
import {
  CoachYearAnalyticsDto,
  ClientAwardDto,
} from "@growth-machine-llc/stridist-api-client";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { useQuery } from "@tanstack/react-query";
import CoachAnalyticsService from "../services/CoachAnalyticsService";
import { useNavigate } from "react-router-dom";
import {
  COACH_CLIENT_EXERCISE_HISTORY_ROUTE,
  COACH_PROGRAM_LIBRARY_PUBLISHED_ROUTE,
  COACH_CLIENT_NUTRITION_ROUTE,
  COACH_BRANDED_APP_ROUTE,
  COACH_CLIENT_PORTAL_ROUTE,
  COACH_TEAM_ROUTE,
} from "../routes/routes";
import {
  normalizeAwardData,
  CLIENT_AWARD_METADATA,
  getAwardGradient,
} from "./year-in-review/year-in-review-constants";
import { Units } from "../constants";
import { YearInReviewLoader } from "./year-in-review/year-in-review-loader";
import { AwardYearInReviewDialog } from "./year-in-review/award-year-in-review-dialog";
import html2canvas from "html2canvas";
import { downloadImage } from "../utils/canvas";
import { isMobileApp } from "../utils/mobile";
import { CircularLoader } from "./loading/CircularLoader";
import { useAnalytics } from "../hooks/useAnalytics";
import { ReactComponent as Logo } from "./new-editor/images/logofull.svg";

export type CustomizableAwards = {
  [key: number]: string;
};

export default function YearInReview({ onClose }: { onClose?: () => void }) {
  const user = useCurrentUser();
  const coachId = user?.id;
  const [year] = useState<number>(2025);
  const [trackEvent] = useAnalytics();

  const [currentSlide, setCurrentSlide] = useState(0);
  const [slideDirection, setSlideDirection] = useState<"left" | "right">(
    "right",
  );
  const [customAwards, setCustomAwards] = useState<CustomizableAwards>({});

  // Autoplay/progress state (ported from your patch)
  const [progress, setProgress] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number>(0);

  const [yearInReviewData, setYearInReviewData] =
    useState<CoachYearAnalyticsDto | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const slideRef = useRef(null);

  const [awardCtaModal, setAwardCtaModal] = useState<{
    award: any;
    path: string;
  } | null>(null);
  const [isSharing, setIsSharing] = useState(false);

  const { data: yearData, isLoading } = useQuery<CoachYearAnalyticsDto | null>({
    queryKey: ["coach-year-analytics", { coachId, year }],
    queryFn: async () => {
      return await CoachAnalyticsService.getCoachYearAnalytics(coachId, year);
    },
    enabled: !!coachId,
  });

  useEffect(() => {
    if (yearData) {
      setYearInReviewData(yearData as CoachYearAnalyticsDto);
      setLoading(false);
    }
  }, [yearData]);

  const slides = useMemo(() => {
    if (!yearInReviewData) return [];

    return [
      { type: "welcome" as const },
      { type: "clientGrowth" as const },
      { type: "contentCreated" as const },
      { type: "nutritionTargets" as const },
      { type: "clientActivity" as const },
      ...yearInReviewData.clientAwards.map((_, index) => ({
        type: "clientAward" as const,
        awardIndex: index,
      })),
      { type: "thankYou" as const },
    ];
  }, [yearInReviewData]);

  const totalSlides = slides.length;

  const SLIDE_DURATION = 5000;
  const PROGRESS_INTERVAL = 50;

  const isEffectivelyPaused = isPaused;
  // Ensure progress resets cleanly whenever slide changes (manual OR auto).
  useEffect(() => {
    if (!yearInReviewData) return;
    if (totalSlides <= 0) return;

    if (isEffectivelyPaused) {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (progressIntervalRef.current)
        clearInterval(progressIntervalRef.current);
      return;
    }

    startTimeRef.current = Date.now() - elapsedTime;

    progressIntervalRef.current = setInterval(() => {
      const currentElapsed = Date.now() - startTimeRef.current;
      setElapsedTime(currentElapsed);

      const currentProgress = (currentElapsed / SLIDE_DURATION) * 100;
      setProgress(currentProgress >= 100 ? 100 : currentProgress);
    }, PROGRESS_INTERVAL);

    const remainingTime = SLIDE_DURATION - elapsedTime;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(
      () => {
        if (
          currentSlide < totalSlides - 1 &&
          !isEffectivelyPaused &&
          !awardCtaModal
        ) {
          handleNext();
        } else if (currentSlide === totalSlides - 1) {
          setProgress(100);
        }
      },
      Math.max(0, remainingTime),
    );

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (progressIntervalRef.current)
        clearInterval(progressIntervalRef.current);
    };
  }, [
    currentSlide,
    isEffectivelyPaused,
    totalSlides,
    elapsedTime,
    yearInReviewData,
  ]);

  const handleNext = () => {
    if (currentSlide < totalSlides - 1) {
      setSlideDirection("right");
      setCurrentSlide(currentSlide + 1);
      setProgress(0);
      setElapsedTime(0);
    }
  };

  const handlePrev = () => {
    if (currentSlide > 0) {
      setSlideDirection("left");
      setCurrentSlide(currentSlide - 1);
      setProgress(0);
      setElapsedTime(0);
    }
  };

  const handleTapNavigation = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const width = rect.width;

    if (x < width / 2) {
      handlePrev();
    } else {
      handleNext();
    }
  };

  const handleClientChange = (awardIndex: number, displayName: string) => {
    setCustomAwards((prev) => ({
      ...prev,
      [awardIndex]: displayName,
    }));
  };

  useEffect(() => {
    if (yearInReviewData && currentSlide >= slides.length) {
      setCurrentSlide(slides.length - 1);
    }
  }, [yearInReviewData, slides.length, currentSlide]);

  const handleReplay = () => {
    setCurrentSlide(0);
    setProgress(0);
    setElapsedTime(0);
    setIsPaused(false);
  };

  const getAwardWithCustomClient = (awardIndex: number) => {
    if (!yearInReviewData) return null;
    const award = yearInReviewData.clientAwards[awardIndex];
    const customClient = customAwards[awardIndex];

    if (customClient) {
      return ClientAwardDto.fromJS({
        ...award.toJSON(),
        displayName: customClient,
        clientAvatar: `/placeholder.svg?height=100&width=100&query=portrait+of+${encodeURIComponent(
          customClient.toLowerCase().replace(" ", "+"),
        )}`,
      });
    }
    return award;
  };

  const handleShare = async () => {
    if (!slideRef.current) return;

    setIsSharing(true);
    try {
      // Temporarily pause autoplay during share
      const wasPlayingBefore = !isPaused;
      if (wasPlayingBefore) {
        setIsPaused(true);
      }

      // Force a reflow to apply the styles
      slideRef.current.offsetHeight;

      // Small delay to ensure styles are applied
      await new Promise(resolve => setTimeout(resolve, 100));

      // Create the canvas from the current slide content
      const canvas = await html2canvas(slideRef.current, {
        useCORS: true,
        allowTaint: true,
        logging: false,
        width: slideRef.current.offsetWidth,
        height: slideRef.current.offsetHeight,
      });

      // Convert to blob and download/share
      canvas.toBlob(async (blob) => {
        if (blob) {
          const slideType = slides[currentSlide]?.type || 'slide';
          const fileName = `year-in-review-${yearInReviewData?.year || year}-${slideType}-${currentSlide + 1}.png`;
          await downloadImage(blob, fileName, "Check out my Year in Review from Stridist!");
          trackEvent('Year in review slide share', { slideType, slideIndex: currentSlide });
        }
      }, 'image/png', 1.0);
    } catch (error) {
      console.error('Error sharing slide:', error);
    } finally {
      setIsSharing(false);
    }
  };

  if (isLoading || !yearInReviewData) {
    return <YearInReviewLoader />;
  }

  const getAwardCtaPath = (cta: string, clientName?: string): string => {
    if (!clientName && (cta.includes("History") || cta.includes("Nutrition"))) {
      return "/";
    }

    switch (cta) {
      case "Learn More about Team Account":
        return COACH_TEAM_ROUTE;

      case "View Exercise History":
        return COACH_CLIENT_EXERCISE_HISTORY_ROUTE.replace(
          ":username",
          clientName || "",
        );

      case "View Program Library":
        return COACH_PROGRAM_LIBRARY_PUBLISHED_ROUTE;

      case "View Nutrition Page":
        return COACH_CLIENT_NUTRITION_ROUTE.replace(
          ":username",
          clientName || "",
        );

      case "Upgrade to CBA Package":
        return COACH_BRANDED_APP_ROUTE;

      default:
        return "/";
    }
  };

  const renderSlide = (forSharing: boolean) => {
    if (!yearInReviewData) return <LinearProgress />;

    const slide = slides[currentSlide];

    switch (slide.type) {
      case "welcome":
        return (
          <WelcomeSlide
            coachName={yearInReviewData.coachName ?? "Coach"}
            year={yearInReviewData.year ?? new Date().getFullYear()}
          />
        );

      case "clientGrowth":
        return (
          <ClientGrowthSlide
            data={{
              startCount:
                yearInReviewData.businessGrowth.clientGrowth.startCount ?? 0,
              endCount:
                yearInReviewData.businessGrowth.clientGrowth.endCount ?? 0,
            }}
          />
        );

      case "contentCreated":
        return (
          <ContentCreatedSlide
            data={{
              workouts: yearInReviewData.businessGrowth.created.workouts ?? 0,
              checkIns: yearInReviewData.businessGrowth.created.checkIns ?? 0,
              lessons: yearInReviewData.businessGrowth.created.lessons ?? 0,
              habits: yearInReviewData.businessGrowth.created.habits ?? 0,
            }}
            year={yearInReviewData.year}
          />
        );

      case "nutritionTargets": {
        const nutritionTargets =
          yearInReviewData.businessGrowth.created.nutritionTargets;
        return (
          <NutritionTargetsSlide
            data={{
              total: nutritionTargets?.total ?? 0,
              types:
                nutritionTargets?.types?.map((t) => ({
                  type: t.type ?? "Unknown",
                  count: t.count ?? 0,
                })) ?? [],
            }}
          />
        );
      }

      case "clientActivity":
        return (
          <ClientActivitySlide
            data={{
              activitiesCompleted:
                yearInReviewData.businessGrowth.clientActivity
                  ?.activitiesCompleted ?? 0,
              totalKgLifted:
                yearInReviewData.businessGrowth.clientActivity?.totalKgLifted ??
                0,
              totalCaloriesEaten:
                yearInReviewData.businessGrowth.clientActivity
                  ?.totalCaloriesEaten ?? 0,
            }}
          />
        );

      case "clientAward": {
        if ("awardIndex" in slide && yearInReviewData) {
          const awardData = getAwardWithCustomClient(slide.awardIndex);
          if (!awardData) return null;

          const coachUnits = (user as any)?.units || Units.METRIC;
          const normalizedAward = normalizeAwardData(awardData, coachUnits);

          const awardForSlide = {
            ...normalizedAward,
            type: normalizedAward.type?.toString() ?? "Unknown",
          };

          return (
            <ClientAwardSlide
              award={awardForSlide}
              awardIndex={slide.awardIndex}
              showCta={!forSharing}
              onCtaClick={(award) => {
                const path = getAwardCtaPath(award.cta, award.clientName);

                if (
                  award.cta === "Learn More about Team Account" ||
                  award.cta === "Upgrade to CBA Package"
                ) {
                  if (timerRef.current) clearTimeout(timerRef.current);
                  if (progressIntervalRef.current)
                    clearInterval(progressIntervalRef.current);
                  const now = Date.now();
                  setElapsedTime(now - startTimeRef.current);

                  setAwardCtaModal({ award, path });
                  setIsPaused(true);
                  return;
                }

                if (onClose) onClose();
                navigate(path);
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
            />
          );
        }
        return null;
      }

      case "thankYou":
        return <ThankYouSlide coachName={yearInReviewData.coachName} forSharing={forSharing} />;

      default:
        return null;
    }
  };

  const handleAwardCtaConfirm = (path: string) => {
    if (onClose) onClose();
    navigate(path);
    window.scrollTo({ top: 0, behavior: "smooth" });
    setAwardCtaModal(null);
    setIsPaused(false);
  };

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        bgcolor: "background.default",
        zIndex: 9999,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header + progress */}
      <Box
        sx={{
          zIndex: 10,
        }}
      >
        {/* Header with title and controls */}
        <Box
          sx={{
            px: 3,
            mb: 2,
            py: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            bgcolor: "#000",
          }}
        >

          {currentSlide !== totalSlides - 1 ? (
            <Box sx={{ color: "white", fontWeight: 700, fontSize: "1rem" }}>
              Year in Review {yearInReviewData?.year ?? year}
            </Box>
          ) : (
            <Button
              startIcon={<Replay />}
              onClick={handleReplay}
              sx={{
                color: "white",
                bgcolor: "rgba(255, 255, 255, 0.1)",
                textTransform: "none",
                fontWeight: 600,
                px: 2,
                py: 0.75,
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  transform: "scale(1.05)",
                  bgcolor: "rgba(255, 255, 255, 0.2)",
                },
              }}
            >
              Replay
            </Button>
          )}


          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            {currentSlide !== 0 && (
              <>
                <Button
                  startIcon={isSharing ? <CircularLoader size={16} /> : isMobileApp ? <Share /> : <Download />}
                  onClick={handleShare}
                  disabled={isSharing}
                  sx={{
                    color: "white",
                    bgcolor: "rgba(255, 255, 255, 0.1)",
                    textTransform: "none",
                    fontWeight: 600,
                    px: 2,
                    py: 0.75,
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                      transform: isSharing ? "none" : "scale(1.05)",
                      bgcolor: "rgba(255, 255, 255, 0.2)",
                    },
                    "&.Mui-disabled": {
                      color: "rgba(255, 255, 255, 0.7)",
                      bgcolor: "rgba(255, 255, 255, 0.1)",
                    },
                  }}
                >
                  {isSharing ? (isMobileApp ? "Sharing..." : "Saving...") : (isMobileApp ? "Share" : "Save")}
                </Button>

                <Box
                  sx={{
                    width: "1px",
                    height: "24px",
                    bgcolor: "rgba(255, 255, 255, 0.3)",
                    mx: 0.5,
                  }}
                />
              </>
            )}

            <IconButton
              onClick={() => setIsPaused(!isPaused)}
              sx={{
                color: "white",
                bgcolor: "rgba(255, 255, 255, 0.1)",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  transform: "scale(1.1)",
                  bgcolor: "rgba(255, 255, 255, 0.2)",
                },
              }}
            >
              {isPaused ? <PlayArrow /> : <Pause />}
            </IconButton>

            {onClose && (
              <IconButton
                onClick={onClose}
                sx={{
                  color: "white",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    transform: "scale(1.1) rotate(90deg)",
                    bgcolor: "rgba(255, 255, 255, 0.1)",
                  },
                }}
              >
                <Close />
              </IconButton>
            )}
          </Box>
        </Box>

        {/* Progress bars below header */}
        <Box
          sx={{
            display: "flex",
            gap: 0.5,
            px: 2,
            pb: 2,
          }}
        >
          {slides.map((_, index) => (
            <Box key={index} sx={{ flex: 1 }}>
              <LinearProgress
                variant="determinate"
                value={
                  index < currentSlide
                    ? 100
                    : index === currentSlide
                      ? progress
                      : 0
                }
                sx={{
                  height: 3,
                  borderRadius: 2,
                  bgcolor: "rgba(255, 255, 255, 0.3)",
                  "& .MuiLinearProgress-bar": {
                    bgcolor: "white",
                    transition:
                      "none"
                  },
                }}
              />
            </Box>
          ))}
        </Box>
      </Box>

      <Box
        onClick={handleTapNavigation}
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          cursor: "pointer",
          overflow: "hidden",
          pt: "28px",
        }}
      >
        <Box
          key={currentSlide}
          sx={{
            width: "100%",
            height: "100%",
            zIndex: 3,
          }}
        >
          {renderSlide(false)}
        </Box>
        <Box
          ref={slideRef}
          key={slides.length + currentSlide + 1}
          className="year-in-review-slide-capture"
          sx={{
            position: "absolute",
            top: "28px",
            left: 0,
            width: "500px",
            height: "888px",
            zIndex: -1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "space-between",
            pointerEvents: "none",
            animation: "none !important",
            "& *": {
              animation: "none !important",
              animationDelay: "0s !important",
              animationDuration: "0s !important",
              transition: "none !important",
              transitionDelay: "0s !important",
              transitionDuration: "0s !important",
            },
            "& *::before, & *::after": {
              animation: "none !important",
              animationDelay: "0s !important",
              animationDuration: "0s !important",
              transition: "none !important",
              transitionDelay: "0s !important",
              transitionDuration: "0s !important",
            }
          }}
        >
          {renderSlide(true)}
          <Box
            sx={{
              position: "absolute",
              top: 20,
              left: "50%",
              transform: "translateX(-50%)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1,
              width: "100%",
            }}
          >
            <Typography
              sx={{
                fontSize: "1rem",
                fontWeight: 600,
                color: "white",
                textAlign: "center",
              }}
            >
              2025 Year in Review
            </Typography>
          </Box>
          <Box sx={{
            position: "absolute",
            bottom: 20,
            left: "50%",
            transform: "translateX(-50%)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            width: "100%",

            "& path": {
              fill: "white",
            }
          }}>

            <Logo />
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          display: { xs: "none", md: "flex" },
          position: "absolute",
          top: "50%",
          left: 0,
          right: 0,
          transform: "translateY(-50%)",
          justifyContent: "space-between",
          px: 2,
          pointerEvents: "none",
        }}
      >
        <IconButton
          onClick={handlePrev}
          disabled={currentSlide === 0}
          sx={{
            pointerEvents: "all",
            bgcolor: "rgba(0, 0, 0, 0.5)",
            color: "white",
            transition: "all 0.2s ease-in-out",
            "&:hover": {
              bgcolor: "rgba(0, 0, 0, 0.7)",
              transform: "scale(1.1) translateX(-4px)",
            },
            "&.Mui-disabled": { display: "none" },
          }}
        >
          <ChevronLeft />
        </IconButton>
        <IconButton
          onClick={handleNext}
          disabled={currentSlide === totalSlides - 1}
          sx={{
            pointerEvents: "all",
            bgcolor: "rgba(0, 0, 0, 0.5)",
            color: "white",
            transition: "all 0.2s ease-in-out",
            "&:hover": {
              bgcolor: "rgba(0, 0, 0, 0.7)",
              transform: "scale(1.1) translateX(4px)",
            },
            "&.Mui-disabled": { display: "none" },
          }}
        >
          <ChevronRight />
        </IconButton>
      </Box>

      <AwardYearInReviewDialog
        open={!!awardCtaModal}
        ctaData={awardCtaModal}
        onClose={() => {
          setAwardCtaModal(null);
          setIsPaused(false);
        }}
        onConfirm={handleAwardCtaConfirm}
        getAwardGradient={getAwardGradient}
      />
    </Box>
  );
}
